import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import Api from '../services/Api'
import { currentCustomerURL } from '../ApiEndpoints'
import { authCleanup, getTokenFromStorage } from '../utils/cleanup'
import { navigate } from 'gatsby'

// Standalone authentication polling hook
export default function useAuthPolling() {
  const { user } = useSelector((state) => state.auth)
  const intervalRef = useRef(null)
  const storageCheckRef = useRef(null)
  const lastTokenRef = useRef(null)
  const isInitializedRef = useRef(false)
  const reloadPreventionRef = useRef(false)

  // Helper function to check if current page is login
  const isLoginPage = () => {
    const path = window.location.pathname;
    return path === '/login' || path === '/login/';
  }

  // Function to perform logout (for cross-tab sync only)
  const performLogout = () => {
    console.log('🚪 Performing cross-tab logout...')
    authCleanup({
      triggerCrossTab: false, // Don't trigger again to avoid loops
      redirectToLogin: true,
      clearSessionData: true
    })
  }

  // Function to handle auth state broadcast
  const broadcastAuthState = (action) => {
    try {
      const message = {
        type: 'AUTH_STATE_CHANGED',
        action,
        timestamp: Date.now(),
        source: window.location.pathname
      };

      // Broadcast to all tabs using postMessage
      window.postMessage(message, window.location.origin);

      // Broadcast to any opener windows
      if (window.opener) {
        window.opener.postMessage(message, window.location.origin);
      }

      // Broadcast through localStorage for older browsers
      localStorage.setItem('auth_broadcast', JSON.stringify(message));
      setTimeout(() => localStorage.removeItem('auth_broadcast'), 1000);
    } catch (error) {
      console.error('Error broadcasting auth state:', error);
    }
  }

  // Function to check authentication and redirect if needed
  const checkAuth = async (shouldRedirect = false) => {
    try {
      const tokenFromStorage = getTokenFromStorage()
      const tokenFromRedux = user?.accessToken
      const currentToken = tokenFromRedux || tokenFromStorage

      if (!currentToken) {
        return null
      }

      const response = await Api.get(currentCustomerURL)

      // If we should redirect and have valid auth, do it
      if (shouldRedirect && response?.data?.id) {
        navigate('/')
      }

      return response.data
    } catch (error) {
      // Let the API interceptor handle 401 errors
      return null
    }
  }

  // Start polling
  const startPolling = () => {
    if (intervalRef.current) return // Already polling

    // Poll every 5 seconds
    intervalRef.current = setInterval(async () => {
      await checkAuth()
    }, 5000)

    // Check for cross-tab token changes every 2 seconds
    storageCheckRef.current = setInterval(() => {
      const currentStorageToken = getTokenFromStorage()
      const lastToken = lastTokenRef.current

      if (currentStorageToken !== lastToken) {
        lastTokenRef.current = currentStorageToken

        // If token was removed (logout), trigger logout
        if (lastToken && !currentStorageToken) {
          console.log('🔄 Token removed detected, triggering logout')
          performLogout()
        }
          // If we got a new token and don't have one in Redux, trigger auth check with redirect
        else if (currentStorageToken && !user?.accessToken) {
          checkAuth(true)
          // Only broadcast if we're not on the login page
          if (!isLoginPage()) {
            broadcastAuthState('LOGIN_SUCCESS')
          }
        }
      }
    }, 2000)
  }

  // Stop polling
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (storageCheckRef.current) {
      clearInterval(storageCheckRef.current)
      storageCheckRef.current = null
    }
  }

  // Listen for cross-tab messages
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        // Verify origin
        if (event.origin !== window.location.origin) return;

        const message = event.data;
        if (!message || typeof message !== 'object') return;

        if (message.type === 'AUTH_STATE_CHANGED') {
        // Prevent handling our own broadcasts
          if (message.source && message.source === window.location.pathname) return;

          // Only handle login messages on the login page
          if (isLoginPage()) {
            if (message.action === 'LOGIN_SUCCESS' && !reloadPreventionRef.current) {
              console.log('🔄 Login detected in another tab, reloading once');
              reloadPreventionRef.current = true;
              window.location.reload();
            }
          }
        }
      } catch (error) {
        console.error('Error handling message:', error);
      }
    };

    // Listen for localStorage broadcast channel
    const handleStorage = (e) => {
      if (e.key === 'auth_broadcast') {
        try {
          const message = JSON.parse(e.newValue || '{}');
          if (!message || typeof message !== 'object') return;

          // Prevent handling our own broadcasts
          if (message.source && message.source === window.location.pathname) return;

          // Only handle login messages on the login page
          if (isLoginPage()) {
            if (message.action === 'LOGIN_SUCCESS' && !reloadPreventionRef.current) {
              console.log('🔄 Login detected through broadcast channel, reloading once');
              reloadPreventionRef.current = true;
              window.location.reload();
            }
          }
        } catch (error) {
          console.error('Error parsing broadcast message:', error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Initialize polling based on token presence
  useEffect(() => {
    const tokenFromStorage = getTokenFromStorage()
    const tokenFromRedux = user?.accessToken
    const hasToken = tokenFromRedux || tokenFromStorage

    // Initialize last token reference
    if (!isInitializedRef.current) {
      lastTokenRef.current = tokenFromStorage
      isInitializedRef.current = true
    }

    if (hasToken) {
      startPolling()
    } else {
      stopPolling()
    }

    return () => {
      stopPolling()
    }
  }, [user?.accessToken]) // Only depend on user.accessToken

  // Function to check if user is authenticated and redirect to home
  const redirectIfAuthenticated = async () => {
    try {
      const tokenFromStorage = getTokenFromStorage()
      const tokenFromRedux = user?.accessToken
      const currentToken = tokenFromRedux || tokenFromStorage

      if (currentToken) {
        // Validate token with API
        const response = await Api.get(currentCustomerURL)
        if (response.data?.id) {
          // Token is valid, redirect to home and broadcast to other tabs
          // Only broadcast if we're not on the login page
          if (!isLoginPage()) {
            broadcastAuthState('LOGIN_SUCCESS')
          }
          if (isLoginPage()) {
            navigate('/')
          }
          return true
        }
      }
      return false
    } catch (error) {
      // Token is invalid, stay on current page
      return false
    }
  }

  return {
    checkAuth,
    redirectIfAuthenticated,
    broadcastAuthState
  }
}
